import { useSessionStore } from '@stores/session/session.store'

import { AUTH_LOGOUT_PATH } from '../core.const'

const TOKEN_CACHE_DURATION = 30 * 60 * 1000
let inFlightTokenPromise: Promise<string> | null = null

function resolveCachedTokenIfValid(): string | null {
  const { token, lastFetched } = useSessionStore.getState()
  if (!token || !lastFetched) {
    return null
  }
  if (Date.now() - lastFetched >= TOKEN_CACHE_DURATION) {
    return null
  }
  return token
}

export function readCachedSessionTokenIfValid(): string | null {
  return resolveCachedTokenIfValid()
}

export async function getSessionToken(): Promise<string> {
  const cached = resolveCachedTokenIfValid()
  if (cached) {
    return cached
  }

  if (inFlightTokenPromise) {
    return inFlightTokenPromise
  }

  inFlightTokenPromise = fetchSessionTokenFromApi().finally(() => {
    inFlightTokenPromise = null
  })

  return inFlightTokenPromise
}

async function fetchSessionTokenFromApi(): Promise<string> {
  const { setToken, setLoading, setError } = useSessionStore.getState()

  try {
    setLoading(true)
    setError(null)

    const res = await fetch('/api/session/token', {
      credentials: 'include',
    })

    if (res.status === 401) {
      window.location.href = AUTH_LOGOUT_PATH
      throw new Error('Unauthorized')
    }

    if (!res.ok) {
      throw new Error(`Failed to fetch token (${res.status})`)
    }

    const newToken = await res.text()

    if (!newToken) {
      throw new Error('Empty token received')
    }

    setToken(newToken)
    return newToken
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch token'
    setError(errorMessage)
    throw error
  } finally {
    setLoading(false)
  }
}
